import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { ButtonProps, Grid, IconButton, Stack } from '@mui/material';
import { merge } from 'lodash-es';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useSwipeable } from 'react-swipeable';
import { StyleObj } from '../../@types';
import useBreakpoints from '../../hooks/useBreakpoints';
import useRefDimensions from '../../hooks/useRefDimensions';
import CarouselScrollIndicator from '../atoms/CarouselScrollIndicator';

import { mergeSx } from '../../utils/styles';

export const commonButtonStyle: ButtonProps['sx'] = {
  position: 'absolute',
  top: 'calc(50% - 20px)',
  zIndex: 10,
  color: (theme) => theme.palette.neutral[200],
  '&:disabled': {
    color: (theme) => theme.palette.neutral[600],
  },
};

const makeStyles = (translateValue: number | undefined, styleOverride: StyleObj, cms: boolean): StyleObj =>
  merge(
    {
      container: {
        p: {
          xs: 0,
          md: cms ? 0 : 3,
        },
        pb: {
          xs: 0,
          md: cms ? 0 : 3,
        },
        '&:hover .cmsButton': {
          transition: 'all .2s ease',
          color: 'white',
        },
      },
      carousel: {
        transition: 'transform 0.5s ease',
        transform: `translateX(${translateValue}px)`,
        display: 'flex',
        width: '100%',
        gap: {
          xs: 0,
          md: cms ? 0 : '24px',
        },
      },
      prevButton: {
        ...commonButtonStyle,
        left: cms ? 0 : -9,
        '&:hover': cms
          ? {
              color: 'info.main',
            }
          : '',
        ...(cms && { className: 'cmsButton' }),
      },
      nextButton: {
        ...commonButtonStyle,
        right: cms ? 0 : 9,
        '&:hover': cms
          ? {
              color: 'info.main',
            }
          : '',
        ...(cms && { className: 'cmsButton' }),
      },
      indicatorContainer: {
        flexDirection: 'row',
        position: 'absolute',
        justifyContent: 'center',
        top: 75,
        right: 10,
        maxWidth: '70%',
      },
    },
    styleOverride
  );

type Props = {
  children: ReactNode;
  cms?: boolean;
  styleOverride?: StyleObj;
  itemsPerStep?: Record<string, number>;
};

const Carousel = ({ children, styleOverride = {}, cms = false }: Props) => {
  const [step, setStep] = useState(0);
  const [maxStep, setMaxStep] = useState(1);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { width } = useRefDimensions(containerRef);
  const styles = makeStyles(-step * width, styleOverride, cms);
  const { isMobile } = useBreakpoints();

  const handlePreviousStep = () => {
    setStep((prevStep) => (prevStep === 0 ? prevStep : prevStep - 1));
  };

  const handleNextStep = () => {
    setStep((prevStep) => prevStep + 1);
  };

  const handlers = useSwipeable({
    onSwiped: (eventData) => {
      if (eventData.dir === 'Right') {
        handlePreviousStep();
      }

      if (eventData.dir === 'Left' && step !== maxStep - 1) {
        handleNextStep();
      }
    },
    trackMouse: true,
  });

  useEffect(() => {
    const children = Array.from(containerRef.current?.children || []);
    let itemsPerStep = 1;

    if (children?.length && width) {
      itemsPerStep = Math.floor(width / children[0].clientWidth);
    }

    if (itemsPerStep > 0) {
      setMaxStep(Math.ceil(children.length / itemsPerStep));
    }
  }, [width]);

  return (
    <Grid
      {...handlers}
      container
      sx={mergeSx(
        {
          justifyContent: 'center',
          position: 'relative',
        },
        styles.container
      )}
    >
      <Grid
        sx={{
          overflow: 'hidden',
        }}
        size={12}
      >
        <Stack direction='row' sx={styles.carousel} ref={containerRef}>
          {children}
        </Stack>
      </Grid>
      {(cms || !isMobile) && (
        <>
          <IconButton disableRipple onClick={handlePreviousStep} disabled={step === 0} sx={styles.prevButton}>
            <ChevronLeft />
          </IconButton>
          {step !== maxStep - 1 && (
            <IconButton disableRipple onClick={handleNextStep} sx={styles.nextButton}>
              <ChevronRight />
            </IconButton>
          )}
        </>
      )}
      {!cms && isMobile && maxStep > 1 && (
        <Stack sx={styles.indicatorContainer}>
          {[...Array(maxStep)].map((_, index) => (
            <CarouselScrollIndicator key={index} isActive={index === step} />
          ))}
        </Stack>
      )}
    </Grid>
  );
};

export default Carousel;
